import React from 'react';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { useSystemAdministrator } from './hooks/useSystemAdministrator';
import AdministrationTable from './components/AdministrationTable';
import AdministrationDialog from './components/AdministrationDialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import AdministrationFilter from './components/AdministrationFilter';

const SystemAdministrator = () => {
    const { th } = useLanguage();
    const toast = useRef(null);

    const {
        // Data
        administrationPages,
        userList,
        roleList,
        selectedAdmin,
        formData,
        displayModal,
        filters,

        // Loading states
        isLoading,
        isLoadingUsers,
        isLoadingRoles,
        isFetchingUsers,
        isLoadingSelectedAdmin,
        isSaving,
        isUpdating,
        isDeleting,
        canUpdateUserRole,

        // Pagination
        pageNumber,
        pageSize,
        totalRecords,

        // Handlers
        handleNew,
        handleEdit,
        handleSave: originalHandleSave,
        handleDelete: originalHandleDelete,
        handleSearch,
        handlePageChange,
        handleSort,
        handleAdvancedFilterChange,
        handleResetFilters,
        closeModal,
        setFormData,

        // Errors
        error
    } = useSystemAdministrator();

    // Wrap handlers with toast notifications
    const handleSave = async () => {
        try {
            await originalHandleSave();
            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: selectedAdmin ? th('systemAdministrator.toastUpdateSuccess') : th('systemAdministrator.toastCreateSuccess'),
                life: 3000
            });
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: error.message || th('systemAdministrator.toastError'),
                life: 3000
            });
        }
    };

    const handleDelete = async () => {
        try {
            await originalHandleDelete();
            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: th('systemAdministrator.toastDeleteSuccess'),
                life: 3000
            });
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: error.message || th('systemAdministrator.toastDeleteError'),
                life: 3000
            });
        }
    };

    React.useEffect(() => {
        if (error) {
            toast.current?.show({
                severity: 'error',
                summary: th('systemAdministrator.toastApiErrorHeader'),
                detail: th('systemAdministrator.toastApiErrorDetail'),
                life: 3000
            });
        }
    }, [error, th]);

    return (
        <div className="card">
            <Toast ref={toast} />

            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('menu.userAuthorization')}</h1>
                
            </div>
             <div className="mb-3">
                <AdministrationFilter
                    initialFilters={filters}
                    onApplyFilters={handleAdvancedFilterChange}
                    onResetFilters={handleResetFilters}
                    initiallyOpen={false}
                    roleList={roleList}
                    isLoadingRoles={isLoadingRoles}
                    isLoadingFilters={isFetchingUsers}  
                    className="advanced-filter-container"
                />
            </div>

            <AdministrationTable
                data={administrationPages}
                loading={isLoading}
                onEdit={handleEdit}
                onNew={handleNew}
                onPageChange={handlePageChange}
                totalRecords={totalRecords}
                pageNumber={pageNumber}
                pageSize={pageSize}
                onSearch={handleSearch}
                onSort={handleSort}
                userList={userList}
            />

            <AdministrationDialog
                visible={displayModal}
                onHide={closeModal}
                selectedAdmin={selectedAdmin}
                formData={formData}
                setFormData={setFormData}
                onSave={handleSave}
                onDelete={handleDelete}
                userList={userList}
                roleList={roleList}
                isLoadingUsers={isLoadingUsers}
                isLoadingRoles={isLoadingRoles}
                isSaving={isSaving}
                isUpdating={isUpdating}
                isDeleting={isDeleting}
                canUpdateUserRole={canUpdateUserRole}
            />
        </div>
    );
};

export default SystemAdministrator;
